/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { forwardRef, Ref, useState } from 'react';
import { DXP_ACTIVE_CLASS } from '../../../../Constants/Design';
import { FormField } from '../../FormField';
import { FORM_FIELD_LABEL_POSITION_LEFT } from '../../FormField.types';
import { ToggleProps, togglePropsDefaults } from './Toggle.props';

/**
 * SDS Toggle component.
 */
export const Toggle = forwardRef((props: ToggleProps, ref: Ref<HTMLButtonElement>): JSX.Element => {
  const { callback, checked, classes, disabled, fieldLabel, readOnly, toggleTextDisable, toggleTextEnable, ...rest } = {
    ...togglePropsDefaults,
    ...props,
  };

  const [isChecked, setChecked] = useState<boolean>(checked);
  const toggleText = isChecked ? toggleTextDisable : toggleTextEnable;
  const toggleClass = clsx(`sds-toggle`, {
    [DXP_ACTIVE_CLASS]: isChecked,
  });

  const onToggleClick = (): void => {
    setChecked(!isChecked);
    callback && callback(!isChecked);
  };

  const toggleBody = (
    <div className={toggleClass} {...rest}>
      <div className="sds-toggle-check"></div>
      <div className="sds-toggle-disc"></div>
    </div>
  );

  // Configure field
  const formFieldProps = { ...props };
  formFieldProps.classes = clsx(`sds-form-toggle`, classes);
  formFieldProps.labelPosition = FORM_FIELD_LABEL_POSITION_LEFT;

  return (
    <FormField {...formFieldProps}>
      <>
        {disabled || readOnly ? (
          <>
            <span
              aria-checked={isChecked}
              aria-label={fieldLabel}
              aria-readonly
              className="sds-toggle-button"
              role="switch"
            >
              {toggleBody}
            </span>
          </>
        ) : (
          <>
            <span className="sr-only">{toggleText}</span>
            <button
              aria-checked={isChecked}
              aria-label={fieldLabel}
              className="sds-toggle-button"
              onClick={(event): void => {
                event.preventDefault();
                onToggleClick();
              }}
              ref={ref}
              role="switch"
            >
              {toggleBody}
            </button>
          </>
        )}
      </>
    </FormField>
  );
});
